/* eslint-disable complexity */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Link, Box, Text } from 'components/ui';
import userUtils from 'utils/User';
import skuUtils from 'utils/Sku';
import { getBiFreeShippingText } from 'utils/getBiFreeShippingText';
import { wrapFunctionalComponent } from 'utils/framework';

import languageLocale from 'utils/LanguageLocale';
import resourceWrapper from 'utils/framework/resourceWrapper';
import { renderModal, globalModals } from 'utils/globalModals';
import { HEADER_VALUE } from 'constants/authentication';
import Actions from 'Actions';
import PreferredZipCodeSelector from 'selectors/user/preferredZipCodeSelector';
import { shipToHomeSelector } from 'selectors/page/product/fulfillmentOptions/deliveryOptions/shipToHome/shipToHomeSelector';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import { showFastShippingCopySignedOutSelector } from 'viewModel/selectors/testTarget/showFastShippingCopySignedOutSelector';
import { showFastShippingCopySignedInSelector } from 'viewModel/selectors/testTarget/showFastShippingCopySignedInSelector';
import DeliveryOptionsEN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US';
import DeliveryOptionsFR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA';

const { preferredZipCodeSelector } = PreferredZipCodeSelector;
const { SHIPPING_AND_HANDLING_INFO } = globalModals;
const { isFRCanada } = languageLocale;
const getText = resourceWrapper(languageLocale.getLocalisedDataFromResource(DeliveryOptionsEN, DeliveryOptionsFR));

function GetItShipped(props) {
    const {
        currentProduct, showBCCMediaModal, fromChooseOptionsModal, sameDayDeliveryClick, onClickAnalytics
    } = props;

    const dispatch = useDispatch();
    const preferredZipCode = useSelector(preferredZipCodeSelector);
    const shipToHome = useSelector(shipToHomeSelector);
    const globalModalsData = useSelector(globalModalsSelector);
    const showFastShippingCopySignedOut = useSelector(showFastShippingCopySignedOutSelector);
    const showFastShippingCopySignedIn = useSelector(showFastShippingCopySignedInSelector);

    const showSignInModal = useCallback(opts => dispatch(Actions.showSignInModal(opts)), [dispatch]);
    const showRegisterModal = useCallback(opts => dispatch(Actions.showRegisterModal(opts)), [dispatch]);
    const showShippingDeliveryLocationModal = useCallback(opts => dispatch(Actions.showShippingDeliveryLocationModal(opts)), [dispatch]);

    const currentSku = currentProduct?.currentSku || {};
    const shouldDisplayEdd = !currentSku.isOutOfStock;
    const isStoreOnly = skuUtils.isStoreOnly(currentSku);
    const locationText = preferredZipCode;
    const getItShippedDeliveryMessage =
        shouldDisplayEdd && shipToHome?.shipToHomeMessage?.length ? shipToHome?.shipToHomeMessage.split(!isFRCanada() ? ' to' : ' au')[0] : '';
    const shippingAndHandlingModal = globalModalsData[SHIPPING_AND_HANDLING_INFO];

    const signInText = getText('signIn');
    const shippingAndReturnsText = getText('shippingAndReturns');
    const createAccountText = getText('createAccount');
    const freeStandardShippingText = getText('freeStandardShipping');
    const isAnonymous = userUtils.isAnonymous();

    return (
        <>
            <Box>
                {shouldDisplayEdd && shipToHome?.shipToHomeMessage?.length ? (
                    <Text
                        is='p'
                        marginBottom='2px'
                    >
                        <Text
                            fontWeight={'bold'}
                            color={'green'}
                            children={getItShippedDeliveryMessage}
                        />
                        {` ${getText('to')} `}
                        <Link
                            onClick={() =>
                                showShippingDeliveryLocationModal({
                                    isOpen: true,
                                    callback: ({ sameDayAvailable }) => {
                                        sameDayDeliveryClick(sameDayAvailable, true);
                                    },
                                    sduZipcodeModal: false,
                                    sku: currentSku
                                })
                            }
                            arrowDirection='down'
                            padding={2}
                            margin={-2}
                            fontWeight='bold'
                            children={locationText}
                        ></Link>
                    </Text>
                ) : null}
                {isAnonymous
                    ? !showFastShippingCopySignedOut && (
                        <span>
                            {getText(
                                'freeShipSignIn',
                                false,
                                <Link
                                    color='blue'
                                    underline={true}
                                    onClick={() => showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } })}
                                    children={signInText}
                                />,
                                <Link
                                    color='blue'
                                    underline={true}
                                    onClick={() => showRegisterModal({ isOpen: true })}
                                    children={createAccountText}
                                />,
                                <strong css={{ display: 'inline-flex' }}>{freeStandardShippingText}</strong>
                            )}
                        </span>
                    )
                    : !showFastShippingCopySignedIn && (
                        <Text
                            is='p'
                            fontSize='sm'
                            children={getBiFreeShippingText(null, true, fromChooseOptionsModal)}
                            marginBottom={[3, 3, 1]}
                            css={{
                                whiteSpace: 'normal'
                            }}
                        />
                    )}
            </Box>
            {!isStoreOnly && (
                <Link
                    color='blue'
                    padding={2}
                    margin={-2}
                    data-at={Sephora.debug.dataAt('pdp_shipping_information_link')}
                    onClick={e => {
                        const bccSpecInfo = {
                            type: 'shipping'
                        };

                        renderModal(shippingAndHandlingModal, () => {
                            showBCCMediaModal(e, bccSpecInfo);
                        });

                        const { sid } = shippingAndHandlingModal;

                        // When the props contains an SID, the fallback function
                        // doesn't fire, and the analytics are not triggered.
                        if (onClickAnalytics && sid) {
                            onClickAnalytics(e, { bccSpecInfo });
                        }
                    }}
                    children={shippingAndReturnsText}
                />
            )}
        </>
    );
}

GetItShipped.propTypes = {
    currentProduct: PropTypes.object,
    showBCCMediaModal: PropTypes.func,
    fromChooseOptionsModal: PropTypes.bool,
    sameDayDeliveryClick: PropTypes.func,
    onClickAnalytics: PropTypes.func
};

export default wrapFunctionalComponent(GetItShipped, 'GetItShipped');
